/* So sánh 3 phiên bản trải nghiệm (profile hiện tại + 2 profile mô phỏng). */
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Icon } from "@/components/icon"
import { SectionTitle } from "@/components/page"
import { cn } from "@/lib/utils"

const percent = (value: unknown) => `${Math.round(Number(value || 0) * 100)}%`
const stageLabel = (stage: string) => (stage === "learn" ? "Giải thích" : stage === "quiz" ? "Quiz" : "Thực hành")
const SCQA = [
  ["S", "Situation", "situation"],
  ["C", "Complication", "complication"],
  ["Q", "Question", "question"],
  ["A", "Answer", "answer"],
] as const

function VariantCard({ variant, index }: { variant: any; index: number }) {
  const profile = variant.profile || {}
  const model = variant.model || {}
  const experience = variant.experience || {}
  const scqa = experience.scqa || {}
  const simulated = !!profile.is_simulated
  const facts = [
    ["Khối lớp", profile.grade_level ? `Lớp ${profile.grade_level}` : "Chưa có dữ liệu"],
    ["Thành thạo", percent(model.mastery)],
    ["Độ chắc chắn", percent(model.confidence)],
    ["Cách học", experience.mode_label || "Có hướng dẫn"],
  ]
  return (
    <Card size="sm" className={cn(!simulated && "border-orange")}>
      <CardContent className="flex flex-col gap-4">
        <header className="flex items-center gap-3">
          <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-soft">
            <Icon name="user" className="size-5" />
          </span>
          <div className="flex min-w-0 flex-col gap-1">
            <Badge variant={simulated ? "secondary" : "solid"}>{simulated ? "Profile mô phỏng · không lưu" : "Profile hiện tại"}</Badge>
            <h4 className="font-heading text-base font-semibold text-ink">{profile.label || `Phiên bản ${index + 1}`}</h4>
          </div>
        </header>
        <dl className="grid grid-cols-2 gap-2">
          {facts.map(([k, v]) => (
            <div key={k} className="rounded-xl bg-paper px-3 py-2">
              <dt className="text-sm text-muted-foreground">{k}</dt>
              <dd className="text-sm font-semibold text-ink">{v}</dd>
            </div>
          ))}
        </dl>
        <section className="flex flex-col gap-2">
          <h5 className="text-sm font-semibold text-ink">SCQA dành cho profile này</h5>
          <dl className="flex flex-col gap-2">
            {SCQA.map(([letter, label, key]) => (
              <div key={key} className="flex gap-3">
                <dt className="grid size-7 shrink-0 place-items-center rounded-lg bg-soft text-sm font-bold text-orange-strong">{letter}</dt>
                <dd className="flex min-w-0 flex-col text-sm">
                  <b className="text-ink">{label}</b>
                  <span className="text-muted-foreground">{scqa[key]}</span>
                </dd>
              </div>
            ))}
          </dl>
        </section>
        <Collapsible>
          <CollapsibleTrigger className="flex w-full items-center justify-between rounded-xl bg-soft px-3 py-2.5 text-sm font-semibold text-accent-foreground">
            Xem lộ trình {experience.steps?.length || 0} bước <Icon name="chevron-down" className="size-4" />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <ol className="flex flex-col gap-2 pt-3">
              {(experience.steps || []).map((step: any) => (
                <li key={step.id} className="flex flex-col text-sm">
                  <b className="text-ink">{step.title}</b>
                  <span className="text-muted-foreground">
                    {step.duration_minutes} phút · {stageLabel(step.stage)}
                  </span>
                </li>
              ))}
            </ol>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  )
}

export function LearningExperienceComparison({ comparison, onClose }: { comparison: any; onClose: () => void }) {
  const variants: any[] = Array.isArray(comparison?.variants) ? comparison.variants : []
  return (
    <section className="flex flex-col gap-4" aria-label="So sánh cá nhân hóa">
      <SectionTitle
        title="Cùng một Package, ba trải nghiệm khác nhau"
        description="Bản đầu tiên dùng Learner Profile hiện tại của con. Hai bản mô phỏng chỉ được workflow tạo để so sánh và không lưu vào database."
        actions={
          <Button variant="outline" size="sm" onClick={onClose}>
            <Icon name="x" className="size-4" /> Đóng
          </Button>
        }
      />
      <div className="grid gap-4 lg:grid-cols-3">
        {variants.map((variant, index) => (
          <VariantCard key={variant.id || index} variant={variant} index={index} />
        ))}
      </div>
    </section>
  )
}
